import { useCallback, useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { fetchInvoiceSourceDocument } from "../../api/invoiceImportApi";
import { loadPdfBytes } from "./invoiceSource";
import { loadPdfDocument, loadPdfDocumentFromUrl } from "./pdfjsViewer";
import type { InvoiceImportSource } from "./types";

type FitMode = "width" | "page" | "custom";

type InvoicePdfViewerProps = {
  importId: string;
  source: InvoiceImportSource;
  hasSourceDocument?: boolean;
};

export function InvoicePdfViewer({ importId, source, hasSourceDocument = true }: InvoicePdfViewerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<PDFDocumentProxy | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [scale, setScale] = useState(1);
  const [fitMode, setFitMode] = useState<FitMode>("width");
  const [openHref, setOpenHref] = useState<string | null>(null);
  const [renderToken, setRenderToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      setStatus("loading");
      setErrorMessage(null);
      try {
        if (hasSourceDocument) {
          const response = await fetchInvoiceSourceDocument(importId);
          if (!response.ok) throw new Error("Could not load stored invoice PDF.");
          const bytes = await response.arrayBuffer();
          if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
          const objectUrl = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
          objectUrlRef.current = objectUrl;
          setOpenHref(objectUrl);
          const doc = await loadPdfDocument(bytes);
          if (cancelled) {
            void doc.destroy();
            return;
          }
          pdfRef.current?.destroy();
          pdfRef.current = doc;
          setPageCount(doc.numPages);
          setStatus("ready");
          setRenderToken((token) => token + 1);
          return;
        }

        if (source.kind === "catalog" && source.url) {
          const doc = await loadPdfDocumentFromUrl(source.url);
          if (cancelled) {
            void doc.destroy();
            return;
          }
          pdfRef.current?.destroy();
          pdfRef.current = doc;
          setOpenHref(source.url);
          setPageCount(doc.numPages);
          setStatus("ready");
          setRenderToken((token) => token + 1);
          return;
        }

        const bytes = await loadPdfBytes(source);
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        const objectUrl = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
        objectUrlRef.current = objectUrl;
        setOpenHref(objectUrl);
        const doc = await loadPdfDocument(bytes);
        if (cancelled) {
          void doc.destroy();
          return;
        }
        pdfRef.current?.destroy();
        pdfRef.current = doc;
        setPageCount(doc.numPages);
        setStatus("ready");
        setRenderToken((token) => token + 1);
      } catch (cause) {
        if (cancelled) return;
        setStatus("error");
        setErrorMessage(cause instanceof Error ? cause.message : "Could not load PDF.");
      }
    }

    void loadPdf();

    return () => {
      cancelled = true;
      pdfRef.current?.destroy();
      pdfRef.current = null;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [hasSourceDocument, importId, source]);

  const renderPages = useCallback(async () => {
    const pdf = pdfRef.current;
    const container = scrollRef.current;
    if (!pdf || !container || status !== "ready") return;

    container.innerHTML = "";
    const containerWidth = container.clientWidth - 24;

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      let pageScale = scale;
      if (fitMode === "width") {
        pageScale = Math.max(0.5, (containerWidth / baseViewport.width) * scale);
      } else if (fitMode === "page") {
        const heightScale = (container.clientHeight - 24) / baseViewport.height;
        pageScale = Math.min(heightScale, containerWidth / baseViewport.width) * scale;
      }
      const viewport = page.getViewport({ scale: pageScale });
      const canvas = document.createElement("canvas");
      canvas.className = "invoice-pdf-page-canvas";
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.setAttribute("role", "img");
      canvas.setAttribute("aria-label", `Invoice page ${pageNumber} of ${pdf.numPages}`);
      const context = canvas.getContext("2d");
      if (!context) continue;
      await page.render({ canvas, canvasContext: context, viewport }).promise;
      const wrap = document.createElement("div");
      wrap.className = "invoice-pdf-page";
      wrap.appendChild(canvas);
      container.appendChild(wrap);
    }
  }, [fitMode, scale, status]);

  useEffect(() => {
    void renderPages();
  }, [renderPages, renderToken]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      if (fitMode !== "custom") {
        setRenderToken((token) => token + 1);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [fitMode]);

  function zoomIn() {
    setFitMode("custom");
    setScale((current) => Math.min(current + 0.15, 3));
  }

  function zoomOut() {
    setFitMode("custom");
    setScale((current) => Math.max(current - 0.15, 0.4));
  }

  function fitWidth() {
    setFitMode("width");
    setScale(1);
  }

  function fitPage() {
    setFitMode("page");
    setScale(1);
  }

  return (
    <div className="invoice-pdf-viewer">
      <div className="invoice-pdf-toolbar">
        <strong className="invoice-pdf-toolbar-title">Original invoice</strong>
        <div className="invoice-pdf-toolbar-actions">
          <button type="button" className="secondary-button invoice-pdf-tool" onClick={zoomOut} aria-label="Zoom out">−</button>
          <button type="button" className="secondary-button invoice-pdf-tool" onClick={zoomIn} aria-label="Zoom in">+</button>
          <button type="button" className="text-button" onClick={fitWidth}>Fit width</button>
          <button type="button" className="text-button" onClick={fitPage}>Fit page</button>
          {openHref && (
            <a className="text-button" href={openHref} target="_blank" rel="noreferrer">
              Open PDF
            </a>
          )}
        </div>
      </div>
      {status === "loading" && <p className="invoice-pdf-status">Loading PDF…</p>}
      {status === "error" && <p className="invoice-import-error">{errorMessage ?? "Could not load PDF."}</p>}
      {status === "ready" && (
        <div ref={scrollRef} className="invoice-pdf-scroll" aria-label={`${source.filename}, ${pageCount} pages`} />
      )}
    </div>
  );
}
